<template>
    <div class="mt-3">
        <div class="w-full text-center">
            <h1 class="text-3xl capitalize">
                Gastos registrados para
                {{ selectedMerchant.merchantType | merchantInfo("type") }}
                <strong class="text-theme-primary">
                    {{ selectedMerchant.name }}
                </strong>
                {{ itemsNumber }}
            </h1>
        </div>

        <div class="flex flex-wrap mt-2">
            <div class="mt-3 w-full">
                <time-filters v-model="query" @enter="reloadItems" />
            </div>
            <div class="flex justify-center mt-2 w-full">
                <app-button v-show="query" relief @click="reloadItems">
                    Consultar 🔎
                </app-button>
            </div>

            <div
                class="flex flex-col lg:flex-row items-center justify-between w-full mt-3 gap-4"
            >
                <div
                    class="flex flex-wrap items-center justify-center lg:justify-start gap-2"
                ></div>

                <div
                    class="flex flex-wrap items-center justify-center lg:justify-end gap-3"
                >
                    <list-settings-button
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />

                    <tooltip-redirect
                        v-if="$ability.hasPermission('cashOutput:Create')"
                        to="/app/cash-outputs/new"
                        message="Registrar gasto"
                    />
                </div>
            </div>
        </div>

        <div class="w-full mt-2">
            <custom-table
                ref="selectableTable"
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                select-mode="single"
                responsive
                selectable
                outlined
                no-border-collapse
                hover
                class="w-full"
                @flex-selected="onRowSelected"
            >
                <template #cell(id)="data">
                    <router-link
                        :to="`/app/cash-outputs/detail/${data.item.id}?merchantType=${data.item.merchantType}&merchantId=${data.item.merchantId}`"
                        class="text-center btn btn-stack-primary p-1"
                    >
                        <span>
                            {{
                                data.item.value === data.item.valuePaid
                                    ? "🟢"
                                    : "⚪"
                            }}
                            &nbsp; {{ data.item.id }}
                        </span>
                    </router-link>
                </template>
                <template #cell(concept)="data">
                    <span class="text-center">
                        {{ data.item.concept | cut(36) }}
                    </span>
                </template>

                <template #cell(supplier)="data">
                    <span class="text-center">
                        {{
                            data.item.supplier ? data.item.supplier.name : "NA"
                        }}
                    </span>
                </template>

                <template #cell(value)="data">
                    <div class="flex flex-col items-center">
                        <span class="text-center font-italic font-bold">
                            {{ data.item.value | money }}
                        </span>
                        <span class="text-center font-italic line-through">
                            &nbsp; {{ data.item.valuePaid | money }}
                        </span>
                    </div>
                </template>

                <template #cell(createdAt)="data">
                    <span class="text-center">
                        {{ data.item.createdAt | date }}
                    </span>
                </template>
            </custom-table>
            <div v-if="!itemsCurrent.length" class="w-11/12 mx-auto mt-4">
                <strong>
                    Nuestro sistema NO encuentra resultados. Intenta modificar
                    los filtros de búsqueda.
                </strong>
            </div>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters, mapState } from "vuex";

import CustomTable from "@/components/utils/CustomTable.vue";
import TimeFilters from "@/components/utils/TimeFilters.vue";
import TooltipRedirect from "@/components/utils/TooltipRedirect.vue";

export default {
    name: "CashOutputsIndex",
    components: { TooltipRedirect, TimeFilters, CustomTable },
    mixins: [urlQuerySyncMixin],
    layout: "app",
    data: () => ({
        query: {},
        itemsCurrent: [],
        $fields: [
            { key: "id", label: "Id", sortable: false },
            { key: "concept", label: "Concepto", sortable: true },
            { key: "supplier", label: "Proveedor", sortable: true },
            { key: "value", label: "Valor/Valor pagado", sortable: true },
            { key: "createdAt", label: "📆 Creación", sortable: true }
        ],
        $urlPaginationKey: "page",
        $urlFilterSchema: [
            { key: "begin", type: "string" },
            { key: "end", type: "string" },
            { key: "agoDays", type: "number" },
            { key: "agoMonths", type: "number" }
        ],

        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24,
        selected: []
    }),
    computed: {
        ...mapState("commons", ["selectedMerchant"]),
        ...mapState("cashOutput", ["cashOutput"]),
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {
        selectedMerchant() {
            this.listItems(true);
        }
    },
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        const pass = await verifyPagePermission(
            "cashOutput:List",
            "/app",
            true
        );
        if (pass) this.listItems(true);
    },
    methods: {
        ...mapActions("cashOutput", ["listCashOutputsPaginated"]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } =
                    await this.listCashOutputsPaginated({
                        complete,
                        itemsByPage: this.pageSize,
                        merchantId: this.selectedMerchant.merchantId,
                        merchantType: this.selectedMerchant.merchantType,
                        page: this.currentPage,
                        ...this.query
                    });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        cloneSelected() {
            window
                .open(
                    `/app/cash-outputs/clone/${this.selected[0].id}`,
                    "_blank"
                )
                .focus();
        },
        clearSelected() {
            this.$refs.selectableTable.clearSelected();
            this.selected = [];
        }
    }
};
</script>
